import { Howl, Howler } from 'howler';
import { computeDistanceFalloff, distance3D } from './audio-distance';
import type { AudioOcclusionSystem } from './audio-occlusion';
import { SoundDesigner, splashDesign } from './sound-designer';
import { getWeaponSoundDesigns } from './weapon-sounds';
import { getEventSoundDesigns } from './event-sounds';
import { getGrenadeSoundDesigns } from './grenade-sounds';
import { getMovementSoundDesigns } from './movement-sounds';

/** Per-sound mixer category. Music is its own subsystem (musicSystem). */
export type SoundCategory = 'sfx' | 'footstep';

/**
 * Auto-classify a sound name into a mixer category.
 * Footsteps are recognised by the `footstep` prefix (`footstep_concrete`,
 * `footstep_metal`, …). Everything else is treated as generic SFX.
 */
export function classifySound(name: string): SoundCategory {
  return /^footstep/i.test(name) ? 'footstep' : 'sfx';
}

/**
 * Central audio manager for Counter Pump.
 * Uses Howler.js for cross-browser audio playback and Web Audio API
 * oscillators as placeholder sounds until real assets are available.
 */
/**
 * A-M-002: minimum interval (ms) between back-to-back plays of the same
 * sound. Only specific names are dedup-gated — entries here suppress
 * second plays inside the window. round_start fires from both the welcome
 * message and the first round_start event on initial join; without this
 * gate the round-start sting plays twice within a few ms.
 *
 * Real rounds are ~30 s apart so the 1 s window never affects legitimate
 * round transitions. Footsteps, gunfire, hit confirms etc. are never
 * dedup-gated because they intentionally overlap in rapid bursts.
 */
const PLAY_MIN_INTERVAL_MS: Record<string, number> = {
  round_start: 1000,
};

export class AudioManager {
  private sounds: Map<string, Howl> = new Map();
  private categories: Map<string, SoundCategory> = new Map();
  /** A-M-002: last successful play timestamp per name (ms). Used only for
   *  entries in PLAY_MIN_INTERVAL_MS. */
  private lastPlayMs: Map<string, number> = new Map();
  private masterVolume: number = 1.0;
  private sfxVolume: number = 0.8;
  private announcerVolume: number = 1.0;
  private footstepVolume: number = 0.7;
  private initialized: boolean = false;
  private listenerPos: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };
  private occlusionSystem: AudioOcclusionSystem | null = null;

  /** Set the audio occlusion system for wall-muffling positional sounds. */
  setOcclusionSystem(system: AudioOcclusionSystem): void {
    this.occlusionSystem = system;
  }

  init(): void {
    if (this.initialized) return;
    Howler.volume(this.masterVolume);
    this.initialized = true;
  }

  /**
   * Best-effort resume of Howler's shared AudioContext.
   *
   * Browsers (especially Safari iOS) start the WebAudio context suspended
   * until a user gesture. Our `audio-unlock.ts` resumes it on first input
   * but server-driven SFX (e.g. round_start, snapshot footsteps) may fire
   * before that gesture. We mirror `MusicSystem.ensureRunning()`: detect
   * the suspended state and fire-and-forget a resume so subsequent
   * `sound.play()` calls produce audible output. F-AV-012.
   */
  private ensureCtxRunning(): void {
    try {
      const ctx: AudioContext | undefined = (Howler as unknown as { ctx?: AudioContext }).ctx;
      if (ctx && ctx.state === 'suspended' && typeof ctx.resume === 'function') {
        // Fire-and-forget — never await on the hot SFX path.
        ctx.resume().catch(() => { /* still suspended; next gesture will retry */ });
      }
    } catch {
      // Howler not yet booted (e.g. in tests with stubs) — no-op.
    }
  }

  /**
   * Apply a per-source biquad lowpass filter to the most recently scheduled
   * Howl playback when `lowpassCutoff < 5000 Hz` (i.e. occlusion is active).
   *
   * Howler does not expose a public per-source filter chain, so we splice a
   * `BiquadFilterNode` between the internal `_panner` and the destination
   * `_node`. This is a best-effort hook: on Howler builds where the internal
   * shape changes, or in test environments without WebAudio nodes, we
   * silently fall through and rely on `volumeMultiplier` alone (which is
   * already applied by the caller). F-AV-002.
   */
  private applyLowpassFilter(sound: Howl, id: number, cutoffHz: number): void {
    if (cutoffHz >= 5000) return; // unoccluded — skip the filter splice
    try {
      const ctx: AudioContext | undefined = (Howler as unknown as { ctx?: AudioContext }).ctx;
      if (!ctx || typeof ctx.createBiquadFilter !== 'function') return;

      // Howler stores per-id sound state in `_sounds`. Each entry exposes the
      // panner and destination nodes we need to splice between.
      const sounds = (sound as unknown as { _sounds?: Array<Record<string, unknown>> })._sounds;
      if (!Array.isArray(sounds)) return;
      const entry = sounds.find((s) => (s as { _id?: number })._id === id);
      if (!entry) return;

      const panner = entry._panner as AudioNode | undefined;
      const node = entry._node as AudioNode | undefined;
      if (!panner || !node) return;

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = Math.max(20, cutoffHz);
      filter.Q.value = 1;

      // Re-route: panner → filter → node (instead of panner → node).
      try { panner.disconnect(node); } catch { /* not yet connected */ }
      panner.connect(filter);
      filter.connect(node);

      // A-M-003: register one-shot cleanup so the filter disconnects when
      // Howl recycles the slot (end of playback or explicit stop). Without
      // this, every occluded play leaks a BiquadFilterNode onto the live
      // WebAudio graph — the leak compounds with each engagement under
      // sustained gunfire/footstep occlusion.
      const cleanup = (): void => {
        try { filter.disconnect(); } catch { /* already disconnected */ }
        try { panner.disconnect(filter); } catch { /* already disconnected */ }
      };
      const howlAny = sound as unknown as {
        once?: (event: string, fn: () => void, id?: number) => void;
      };
      if (typeof howlAny.once === 'function') {
        howlAny.once('end', cleanup, id);
        howlAny.once('stop', cleanup, id);
      }
    } catch {
      // Best-effort — fall back to volume-only occlusion.
    }
  }

  /**
   * Update the listener position for distance-based volume falloff.
   * Call this every frame with the local player's camera position.
   */
  setListenerPosition(x: number, y: number, z: number): void {
    this.listenerPos = { x, y, z };
  }

  /**
   * Generate a short oscillator tone and return it as a base64 WAV data URI.
   * This lets us create Howl instances without needing actual audio files.
   */
  private generateTone(
    frequency: number,
    durationSec: number,
    type: OscillatorType = 'sine',
    volume: number = 0.5,
  ): string {
    const sampleRate = 44100;
    const numSamples = Math.floor(sampleRate * durationSec);
    const numChannels = 1;
    const bitsPerSample = 16;
    const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
    const blockAlign = numChannels * (bitsPerSample / 8);
    const dataSize = numSamples * blockAlign;

    // Build WAV buffer
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    // WAV header
    const writeStr = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true); // chunk size
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeStr(36, 'data');
    view.setUint32(40, dataSize, true);

    // Generate samples
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const phase = 2 * Math.PI * frequency * t;
      let sample: number;

      switch (type) {
        case 'sine':
          sample = Math.sin(phase);
          break;
        case 'square':
          sample = Math.sin(phase) >= 0 ? 1 : -1;
          break;
        case 'sawtooth':
          sample = 2 * ((frequency * t) % 1) - 1;
          break;
        case 'triangle': {
          const p = (frequency * t) % 1;
          sample = 4 * Math.abs(p - 0.5) - 1;
          break;
        }
        default:
          sample = Math.sin(phase);
      }

      // Apply volume and a quick fade-out envelope to avoid clicks
      const envelope = i < numSamples * 0.1
        ? i / (numSamples * 0.1) // attack
        : 1 - ((i - numSamples * 0.1) / (numSamples * 0.9)); // decay
      sample *= volume * Math.max(0, envelope);

      // Clamp and write 16-bit PCM
      const clamped = Math.max(-1, Math.min(1, sample));
      view.setInt16(44 + i * 2, clamped * 0x7fff, true);
    }

    // Convert to base64 data URI
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return 'data:audio/wav;base64,' + btoa(binary);
  }

  private generateAdvancedSound(
    layers: Array<{
      frequency: number;
      endFrequency?: number;
      type: OscillatorType | 'noise';
      volume: number;
      startTime?: number;
      endTime?: number;
      attack?: number;
      decay?: number;
    }>,
    durationMs: number,
  ): string {
    const sampleRate = 44100;
    const durationSec = durationMs / 1000;
    const numSamples = Math.floor(sampleRate * durationSec);
    const numChannels = 1;
    const bitsPerSample = 16;
    const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
    const blockAlign = numChannels * (bitsPerSample / 8);
    const dataSize = numSamples * blockAlign;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    const writeStr = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeStr(36, 'data');
    view.setUint32(40, dataSize, true);
    let noiseSeed = 12345;
    const pseudoRandom = (): number => {
      noiseSeed = (noiseSeed * 16807 + 0) % 2147483647;
      return (noiseSeed / 2147483647) * 2 - 1;
    };
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const normalizedPos = i / numSamples;
      let mixedSample = 0;
      for (const layer of layers) {
        const layerStart = layer.startTime ?? 0;
        const layerEnd = layer.endTime ?? 1;
        if (normalizedPos < layerStart || normalizedPos > layerEnd) continue;
        const layerDuration = layerEnd - layerStart;
        const layerPos = layerDuration > 0 ? (normalizedPos - layerStart) / layerDuration : 0;
        const attackEnd = layer.attack ?? 0.05;
        const decayStart = 1 - (layer.decay ?? 0.3);
        let envelope: number;
        if (layerPos < attackEnd) {
          envelope = attackEnd > 0 ? layerPos / attackEnd : 1;
        } else if (layerPos > decayStart) {
          const decayLen = 1 - decayStart;
          envelope = decayLen > 0 ? 1 - (layerPos - decayStart) / decayLen : 0;
        } else {
          envelope = 1;
        }
        envelope = Math.max(0, Math.min(1, envelope));
        const freq = layer.endFrequency !== undefined
          ? layer.frequency + (layer.endFrequency - layer.frequency) * layerPos
          : layer.frequency;
        let sample: number;
        if (layer.type === 'noise') {
          sample = pseudoRandom();
        } else {
          const phase = 2 * Math.PI * freq * t;
          switch (layer.type) {
            case 'sine': sample = Math.sin(phase); break;
            case 'square': sample = Math.sin(phase) >= 0 ? 1 : -1; break;
            case 'sawtooth': sample = freq > 0 ? 2 * ((freq * t) % 1) - 1 : 0; break;
            case 'triangle': {
              if (freq > 0) { const p = (freq * t) % 1; sample = 4 * Math.abs(p - 0.5) - 1; }
              else { sample = 0; }
              break;
            }
            default: sample = Math.sin(phase);
          }
        }
        mixedSample += sample * layer.volume * envelope;
      }
      if (mixedSample > 1) mixedSample = 1 - 1 / (1 + mixedSample);
      else if (mixedSample < -1) mixedSample = -(1 - 1 / (1 - mixedSample));
      const clamped = Math.max(-1, Math.min(1, mixedSample));
      view.setInt16(44 + i * 2, clamped * 0x7fff, true);
    }
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return 'data:audio/wav;base64,' + btoa(binary);
  }

  private registerAdvancedSound(
    name: string,
    layers: Array<{
      frequency: number;
      endFrequency?: number;
      type: OscillatorType | 'noise';
      volume: number;
      startTime?: number;
      endTime?: number;
      attack?: number;
      decay?: number;
    }>,
    durationMs: number,
  ): void {
    const dataUri = this.generateAdvancedSound(layers, durationMs);
    const howl = new Howl({ src: [dataUri], format: ['wav'], volume: this.sfxVolume, preload: true });
    this.sounds.set(name, howl);
    this.categories.set(name, classifySound(name));
  }

  /**
   * Register a placeholder sound generated from an oscillator tone.
   *
   * Wire-Not-Delete (F-AV-010): this entry point is a dev/test fixture used
   * by `client/src/__tests__/audio-mixer.test.ts` to assert per-category
   * mixer behaviour without booting the full SoundDesigner pipeline.
   * Production registers sounds through `registerGameSounds()` which uses
   * the SoundDesigner multi-layer renderer. Do not delete — removing this
   * breaks the audio-mixer category/volume coverage.
   */
  registerPlaceholder(
    name: string,
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    volume: number = 0.5,
  ): void {
    const dataUri = this.generateTone(frequency, duration / 1000, type, volume);
    const howl = new Howl({
      src: [dataUri],
      format: ['wav'],
      volume: this.sfxVolume,
      preload: true,
    });
    this.sounds.set(name, howl);
    this.categories.set(name, classifySound(name));
  }

  /**
   * Play a sound by name.
   *
   * Master volume is applied globally via Howler.volume(). Per-category gain
   * (sfx, footstep) is multiplied into the per-instance volume:
   *   final = (volume ?? 1) * sfxVolume * categoryMultiplier
   *
   * @returns The Howl sound ID, or -1 if the sound was not found.
   */
  play(name: string, volume?: number): number {
    const sound = this.sounds.get(name);
    if (!sound) {
      console.warn(`[AudioManager] Sound not found: ${name}`);
      return -1;
    }
    // A-M-002: dedup gate for names listed in PLAY_MIN_INTERVAL_MS. The only
    // entry today is `round_start` — both the welcome handler and the first
    // round_start event fire the SFX on initial join. Suppress the second
    // call inside the 1 s window so only one plays.
    const minInterval = PLAY_MIN_INTERVAL_MS[name];
    if (minInterval !== undefined) {
      const now = Date.now();
      const last = this.lastPlayMs.get(name);
      if (last !== undefined && now - last < minInterval) {
        return -1;
      }
      this.lastPlayMs.set(name, now);
    }
    // F-AV-012: kick the shared AudioContext if browsers paused it.
    this.ensureCtxRunning();
    const id = sound.play();
    const cat = this.categories.get(name) ?? 'sfx';
    const catMul = cat === 'footstep' ? this.footstepVolume : 1.0;
    if (volume !== undefined || catMul !== 1.0) {
      const base = volume ?? 1.0;
      sound.volume(base * this.sfxVolume * catMul, id);
    }
    return id;
  }

  /**
   * Returns true when a sound is registered. Lets callers route to a class-
   * specific variant when one exists and fall back to the universal sound
   * otherwise (W8 — per-weapon reload/inspect routing).
   */
  has(name: string): boolean {
    return this.sounds.has(name);
  }

  /**
   * Play a sound with 3D spatial positioning and distance-based volume falloff.
   *
   * CS:GO-style distance model:
   * - Full volume within 2m
   * - Linear falloff from 2m to maxDist (20m for footsteps, 40m for gunfire)
   * - Silent beyond maxDist (returns -1, sound is not played)
   *
   * @returns The Howl sound ID, or -1 if the sound was not found or inaudible.
   */
  playAt(
    name: string,
    position: { x: number; y: number; z: number },
    volume?: number,
  ): number {
    const sound = this.sounds.get(name);
    if (!sound) {
      console.warn(`[AudioManager] Sound not found: ${name}`);
      return -1;
    }

    // Calculate distance-based volume falloff
    const dist = distance3D(position, this.listenerPos);
    const { factor } = computeDistanceFalloff(dist, name);

    // Apply audio occlusion (wall muffling)
    let occlusionFactor = 1.0;
    let occlusionCutoff = 5000;
    if (this.occlusionSystem) {
      const result = this.occlusionSystem.query(this.listenerPos, position);
      occlusionFactor = result.volumeMultiplier;
      // F-AV-002: keep the cutoff alongside the volume — lowpass is what
      // makes occlusion *sound* like wall muffling instead of just quiet.
      occlusionCutoff = result.lowpassCutoff;
    }

    const baseVolume = volume ?? 1.0;
    const finalVolume = baseVolume * factor * occlusionFactor;

    // Don't play inaudible sounds (saves CPU + audio channels)
    if (finalVolume < 0.01) return -1;

    // F-AV-012: ensure WebAudio context is running before play.
    this.ensureCtxRunning();

    const id = sound.play();
    sound.pos(position.x, position.y, position.z, id);
    const cat = this.categories.get(name) ?? 'sfx';
    const catMul = cat === 'footstep' ? this.footstepVolume : 1.0;
    sound.volume(finalVolume * this.sfxVolume * catMul, id);
    // F-AV-002: splice a BiquadFilterNode lowpass when occlusion is active.
    this.applyLowpassFilter(sound, id, occlusionCutoff);
    return id;
  }

  /**
   * Stop a specific sound by name.
   */
  stop(name: string): void {
    const sound = this.sounds.get(name);
    if (sound) {
      sound.stop();
    }
  }

  /**
   * Stop all currently playing sounds.
   */
  stopAll(): void {
    Howler.stop();
  }

  setMasterVolume(vol: number): void {
    this.masterVolume = Math.max(0, Math.min(1, vol));
    Howler.volume(this.masterVolume);
  }

  setSfxVolume(vol: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, vol));
    // Propagate to already-registered Howl instances so future plays without
    // an explicit volume use the new default — and any currently playing
    // sounds switch to the new level immediately.
    for (const sound of this.sounds.values()) {
      try { sound.volume(this.sfxVolume); } catch { /* ignore unloaded */ }
    }
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  getSfxVolume(): number {
    return this.sfxVolume;
  }

  /**
   * Announcer / VO volume. Stored on the manager so the settings → audio
   * wiring can read a single source of truth, but the actual TTS volume is
   * applied by `announcer.setVolume()` (see audio/announcer.ts).
   */
  setAnnouncerVolume(vol: number): void {
    this.announcerVolume = Math.max(0, Math.min(1, vol));
  }

  getAnnouncerVolume(): number {
    return this.announcerVolume;
  }

  /**
   * Footstep volume — multiplied with sfxVolume on every footstep play().
   * Lets players quiet down their own footstep mix without affecting weapons.
   */
  setFootstepVolume(vol: number): void {
    this.footstepVolume = Math.max(0, Math.min(1, vol));
  }

  getFootstepVolume(): number {
    return this.footstepVolume;
  }

  /** Lookup the mixer category a registered sound belongs to. */
  getCategoryFor(name: string): SoundCategory | undefined {
    return this.categories.get(name);
  }

  /**
   * Pre-register all game sound effects as oscillator-generated placeholders.
   * These can be swapped out for real audio assets later by replacing the
   * registerPlaceholder calls with Howl instances pointing to actual files.
   */
  registerGameSounds(): void {
    // Use the SoundDesigner multi-layer rendering engine for all sounds.
    // Each *-sounds.ts file defines rich multi-layer designs with envelopes,
    // filters, distortion, and frequency sweeps — far better than placeholders.
    const allDesigns = [
      getWeaponSoundDesigns(),
      getEventSoundDesigns(),
      getGrenadeSoundDesigns(),
      getMovementSoundDesigns(),
    ];
    for (const designMap of allDesigns) {
      for (const [name, design] of designMap) {
        const dataUri = SoundDesigner.renderToDataUri(design);
        const howl = new Howl({
          src: [dataUri],
          format: ['wav'],
          volume: this.sfxVolume,
          preload: true,
        });
        this.sounds.set(name, howl);
        this.categories.set(name, classifySound(name));
      }
    }

    // Phase B3 — water splash SFX. Triggered on is_in_water rising edge
    // by the renderer feeding per-tick player snapshots into a
    // WaterEdgeTracker (see sound-designer.ts).
    const splashUri = SoundDesigner.renderToDataUri(splashDesign);
    this.sounds.set('splash', new Howl({
      src: [splashUri],
      format: ['wav'],
      volume: this.sfxVolume,
      preload: true,
    }));
    this.categories.set('splash', classifySound('splash'));
  }

  /**
   * Clean up all sounds and release resources.
   */
  dispose(): void {
    this.sounds.forEach((sound) => {
      sound.unload();
    });
    this.sounds.clear();
    this.categories.clear();
    Howler.unload();
    this.sounds = new Map();
    this.initialized = false;
  }
}

export const audioManager = new AudioManager();
